/**
 * Map Component
 *
 * Displays events on a Leaflet map with custom markers for each event type.
 * Performance optimizations:
 * - Icon caching by type+severity
 * - React.memo for preventing unnecessary re-renders
 * - Wind data caching
 * - Viewport-based filtering to limit rendered markers
 */

import { useEffect, useRef, useState, memo, useMemo, lazy, Suspense } from 'react';
import { MapContainer, Marker, Popup, Circle, Pane, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { UnifiedEvent, EVENT_TYPE_INFO, formatRelativeTime, WindData, getWindDirection, GeoPhoto } from '../types/events';
import { fetchWindData } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { statusColor } from '../utils/severity';
import { isLowEndDevice } from '../utils/device';

// WindParticleLayer (865 lines) only renders when the user toggles wind on.
// Most users keep it off — lazy-load to defer the cost.
const WindParticleLayer = lazy(() => import('./WindParticleLayer'));
import AircraftLayer from './AircraftLayer';
import PhotoMarkerLayer from './PhotoMarkerLayer';
import FireBoundaryLayer from './FireBoundaryLayer';
import HotspotLayer from './HotspotLayer';
import RoadClosureLayer from './RoadClosureLayer';
import PowerOutageLayer from './PowerOutageLayer';
import SmokeLayer from './SmokeLayer';
import SatelliteImageryLayer from './SatelliteImageryLayer';
import AirQualityLayer from './AirQualityLayer';
import CityTemperatureLayer from './CityTemperatureLayer';
import DataFreshnessChip from './DataFreshnessChip';
import ZoneDrawingLayer, { ZoneShape, DrawnZone } from './ZoneDrawingLayer';
import WatchZoneOverlay from './WatchZoneOverlay';
import ThunderstormWarningLayer from './ThunderstormWarningLayer';
import LightningRiskLayer from './LightningRiskLayer';
import { useSupporter } from '../contexts/SupporterContext';
import SupporterModal from './SupporterModal';
import WarningAreaLayer from './WarningAreaLayer';
import { MapLayerIcon, FireMarkerIcon } from './icons';
import { WatchZone } from '../api/client';

// Fix Leaflet default marker icon issue with bundlers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { isTouchPhoneNow } from '../hooks/useIsTouchPhone';

// @ts-expect-error Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Icon cache to prevent regenerating identical icons
const iconCache: Record<string, L.DivIcon> = {};

// Wind data cache to prevent redundant API calls
const windDataCache: Record<string, WindData> = {};

/** Map bounds interface for filtering visible events */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface MapProps {
  events: UnifiedEvent[];
  center: { lat: number; lon: number };
  viewRadiusKm?: number;
  recenterTrigger?: number;
  isVisible?: boolean;
  onEventClick?: (event: UnifiedEvent) => void;
  onBoundsChange?: (bounds: MapBounds) => void;
  showWindLayer?: boolean;
  showAircraftLayer?: boolean;
  showFireBoundaries?: boolean;
  showWarningAreas?: boolean;
  showHotspots?: boolean;
  showRoadClosures?: boolean;
  showPowerOutages?: boolean;
  showSmoke?: boolean;
  showAirQuality?: boolean;
  showCityTemperatures?: boolean;
  // Lightning & Thunderstorm layers
  showThunderstormWarnings?: boolean;
  showLightningRisk?: boolean;
  // Supporter-gated premium imagery. The overlay is only added when the
  // matching entitlement (highResLayers / satelliteImagery) is also present —
  // these booleans are just the user's toggle preference.
  showHighResImagery?: boolean;
  showSatelliteImagery?: boolean;
  selectedEvent?: UnifiedEvent | null;
  photos?: GeoPhoto[];
  onRequestRemoval?: (photoId: string, fileName: string) => void;
  onMyLocation?: () => void;
  isLocating?: boolean;
  // Watch zone editor integration
  onMapClick?: (lat: number, lon: number) => void;
  editorPreviewCircle?: {
    center: { lat: number; lon: number };
    radiusKm: number;
  } | null;
  // Polygon drawing integration
  isDrawingZone?: boolean;
  zoneShapeType?: ZoneShape;
  onZoneDrawn?: (zone: DrawnZone) => void;
  editingZone?: DrawnZone | null;
  // Watch zone visualization
  watchZones?: WatchZone[];
  visibleZoneIds?: Set<string>;
  onZoneClick?: (zone: WatchZone) => void;
  onZoneEdit?: (zone: WatchZone) => void;
  // 3D view integration
  onView3D?: (event: UnifiedEvent) => void;
  onRequestAuth?: () => void;
  // Mobile: close popup when clicking map background
  onDeselectEvent?: () => void;
  // User location for blue dot
  userLocation?: { lat: number; lon: number } | null;
  /** Called when the user clicks "Ride Along" on an aircraft popup —
   *  switches to 3D and enters cockpit mode for that icao24. */
  onAircraftRideAlong?: (icao24: string) => void;
}

/**
 * Create a custom divIcon for an event type.
 * Fire incidents use RFS-style markers:
 * - White diamond for "Not Applicable" alert level
 * - Yellow triangle for "Advice"
 * - Orange triangle for "Watch and Act"
 * - Red triangle for "Emergency"
 *
 * PERFORMANCE: Icons are cached by type+severity to avoid regenerating SVG strings
 */
function createEventIcon(event: UnifiedEvent): L.DivIcon {
  const info = EVENT_TYPE_INFO[event.type];
  const severity = event.data?.severity as string | undefined;
  // Planned-burn / hazard-reduction marker variant — same shape and colour
  // as a regular fire of the same severity, but with a dashed/dotted
  // border so users can tell scheduled burns apart from wildfires (matches
  // Hazards Near Me convention). Backend tags this during ingestion.
  const isPlannedBurn = event.data?.isPlannedBurn === true;
  const dashAttr = isPlannedBurn ? ' stroke-dasharray="4,2.5"' : '';
  const dashAttrDiamond = isPlannedBurn ? ' stroke-dasharray="3,2"' : '';

  // Cache key includes planned-burn flag so a fire and its planned-burn
  // counterpart at the same severity don't share an SVG.
  const cacheKey = `${event.type}-${severity || 'default'}-${isPlannedBurn ? 'pb' : 'fr'}`;

  // Return cached icon if available
  if (iconCache[cacheKey]) {
    return iconCache[cacheKey];
  }

  let icon: L.DivIcon;

  // For fire incidents, use RFS-style markers
  if (event.type === 'FIRE_INCIDENT') {

    // SVG flame icon - color varies by severity
    const getFlameIcon = (flameColor: string) => `
      <path d="M12 4C12 4 5 10 5 14.5C5 18 7.5 20 10.5 20C10.5 20 8 18 8 15.5C8 13 10 11 10 11C10 11 9 14 11 16C11 16 10 13 12 10C14 13 13 16 13 16C15 14 14 11 14 11C14 11 16 13 16 15.5C16 18 13.5 20 13.5 20C16.5 20 19 18 19 14.5C19 10 12 4 12 4Z"
            fill="${flameColor}"/>
    `;

    // NOT_APPLICABLE uses a white diamond shape (like RFS map)
    if (severity === 'NOT_APPLICABLE') {
      const flameIcon = getFlameIcon('black');
      const diamondSvg = `
        <svg viewBox="0 0 32 32" width="32" height="32" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
          <!-- Diamond shape (rotated square) -->
          <rect x="4" y="4" width="24" height="24" transform="rotate(45 16 16)"
                fill="white" stroke="black" stroke-width="2"${dashAttrDiamond}/>
          <!-- Fire icon centered (scale 0.85 to match triangle markers) -->
          <g transform="translate(6, 4) scale(0.85)">
            ${flameIcon}
          </g>
        </svg>
      `;

      icon = L.divIcon({
        className: 'custom-event-marker fire-diamond',
        html: diamondSvg,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
        popupAnchor: [0, -16],
      });
    } else {
      // Severity colors for triangles: EMERGENCY=dark red, WATCH_AND_ACT=orange, ADVICE=yellow
      let triangleColor = '#fbbf24'; // Default yellow (Advice)
      let flameColor = 'black'; // Default black flame

      if (severity === 'EMERGENCY') {
        triangleColor = '#b91c1c'; // Darker red for better contrast
        flameColor = 'white'; // White flame on dark red
      } else if (severity === 'WATCH_AND_ACT') {
        triangleColor = '#f97316'; // Orange
      } else if (severity === 'ADVICE') {
        triangleColor = '#fbbf24'; // Yellow/Amber
      }

      const flameIcon = getFlameIcon(flameColor);

      // SVG hazard triangle with fire icon inside
      const triangleSvg = `
        <svg viewBox="0 0 40 36" width="40" height="36" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
          <!-- Triangle background with black outline -->
          <path d="M20 2 L38 34 L2 34 Z" fill="${triangleColor}" stroke="black" stroke-width="2.5"${dashAttr}/>
          <!-- Fire icon centered -->
          <g transform="translate(10, 10) scale(0.8)">
            ${flameIcon}
          </g>
        </svg>
      `;

      icon = L.divIcon({
        className: 'custom-event-marker fire-hazard',
        html: triangleSvg,
        iconSize: [40, 36],
        iconAnchor: [20, 34], // Anchor at bottom center of triangle
        popupAnchor: [0, -30],
      });
    }
  } else {
    // For non-fire events, use the standard circular icon
    const bgColor = info.color;
    const iconToShow = info.icon;

    icon = L.divIcon({
      className: 'custom-event-marker',
      html: `
        <div style="
          background-color: ${bgColor};
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ">
          ${iconToShow}
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
    });
  }

  // Cache and return the icon
  iconCache[cacheKey] = icon;
  return icon;
}

/**
 * Calculate appropriate zoom level for a given radius in km.
 * Based on the fact that at zoom level 0, the world is 256px wide (~40,075 km).
 */
function getZoomForRadius(radiusKm: number): number {
  // Approximate zoom levels for different radii
  // These values give a good view where the radius fits comfortably in the viewport
  if (radiusKm >= 3000) return 5;  // Australia-wide view (zoom 5 fits continent well)
  if (radiusKm >= 1000) return 5;
  if (radiusKm >= 500) return 6;
  if (radiusKm >= 200) return 7;
  if (radiusKm >= 100) return 8;
  if (radiusKm >= 50) return 9;
  if (radiusKm >= 25) return 10;
  if (radiusKm >= 10) return 11;
  if (radiusKm >= 5) return 12;
  if (radiusKm >= 3) return 13;
  if (radiusKm >= 1.5) return 15;
  return 16; // Street/building-level for a specific searched address
}

/**
 * Component to recenter map when center prop changes.
 * Uses flyTo for smooth animation and calculates appropriate zoom based on viewRadius.
 */
function MapRecenter({ center, viewRadiusKm, recenterTrigger }: { center: { lat: number; lon: number }; viewRadiusKm?: number; recenterTrigger?: number }) {
  const map = useMap();
  const lastCenterRef = useRef<{ lat: number; lon: number } | null>(null);
  const lastTriggerRef = useRef<number>(recenterTrigger ?? 0);

  useEffect(() => {
    // Check if this is a significant center change (new location selected)
    const lastCenter = lastCenterRef.current;
    const triggerChanged = recenterTrigger !== undefined && recenterTrigger !== lastTriggerRef.current;
    const isNewLocation = triggerChanged || !lastCenter ||
      Math.abs(lastCenter.lat - center.lat) > 0.001 ||
      Math.abs(lastCenter.lon - center.lon) > 0.001;

    lastCenterRef.current = { lat: center.lat, lon: center.lon };
    if (recenterTrigger !== undefined) lastTriggerRef.current = recenterTrigger;

    if (isNewLocation && viewRadiusKm) {
      // Fly to new location with appropriate zoom for the area
      const targetZoom = getZoomForRadius(viewRadiusKm);
      map.flyTo([center.lat, center.lon], targetZoom, {
        duration: 1.5,
      });
    } else if (isNewLocation) {
      // No viewRadius provided, just pan without changing zoom
      map.setView([center.lat, center.lon], map.getZoom());
    }
  }, [center.lat, center.lon, viewRadiusKm, recenterTrigger, map]);

  return null;
}

/**
 * Component to handle map click events.
 * Used when the watch zone editor is open to set the zone center.
 */
function MapClickHandler({ onClick }: { onClick?: (lat: number, lon: number) => void }) {
  useMapEvents({
    click: (e) => {
      if (onClick) {
        onClick(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

/**
 * Component to handle map background clicks for deselecting events.
 * Used on mobile to close fire event cards when clicking the map background.
 * Note: Marker clicks in Leaflet do NOT propagate to the map, so this only fires
 * when clicking empty map areas.
 */
function MapDeselectHandler({ onDeselect }: { onDeselect: () => void }) {
  useMapEvents({
    click: () => {
      onDeselect();
    },
  });
  return null;
}

/**
 * Component to handle map visibility changes (mobile view toggle).
 * Invalidates the map size when becoming visible to ensure tiles load correctly.
 */
function MapVisibilityHandler({ isVisible }: { isVisible: boolean }) {
  const map = useMap();
  const wasVisibleRef = useRef(isVisible);

  useEffect(() => {
    // Only invalidate when transitioning from hidden to visible
    if (isVisible && !wasVisibleRef.current) {
      // Small delay to allow CSS transition to complete
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }
    wasVisibleRef.current = isVisible;
  }, [isVisible, map]);

  return null;
}

/**
 * Component to fly to a selected event location.
 * Offsets the center point slightly south to account for the header and popup.
 * Only flies once per event selection - user can freely pan afterwards.
 */
function FlyToEvent({ event }: { event: UnifiedEvent | null | undefined }) {
  const map = useMap();
  const lastFlyToIdRef = useRef<string | null>(null);
  // Store event data in ref to avoid dependency on the whole event object
  const eventDataRef = useRef<{ id: string; lat: number; lon: number } | null>(null);

  // Update ref when event changes
  if (event) {
    eventDataRef.current = {
      id: event.id,
      lat: event.location.lat,
      lon: event.location.lon,
    };
  } else {
    eventDataRef.current = null;
  }

  // Only depend on event.id to prevent re-flying when event object reference changes
  const eventId = event?.id ?? null;

  useEffect(() => {
    const eventData = eventDataRef.current;

    // Only fly if this is a new event (different from the last one we flew to)
    if (eventData && eventData.id !== lastFlyToIdRef.current) {
      lastFlyToIdRef.current = eventData.id;

      // Calculate offset to account for header and popup height
      // Mobile popups are taller, so need more offset
      const isMobile = isTouchPhoneNow();
      const targetZoom = 14;
      const targetPoint = map.project([eventData.lat, eventData.lon], targetZoom);

      // Offset the view so the marker + popup are centered vertically
      // Mobile: larger offset for taller/wider popups (popup appears above marker)
      // Desktop: smaller offset
      const offsetY = isMobile ? -240 : -120;
      const offsetPoint = L.point(targetPoint.x, targetPoint.y + offsetY);
      const offsetLatLng = map.unproject(offsetPoint, targetZoom);

      map.flyTo(offsetLatLng, targetZoom, {
        duration: 1.5,
      });
    }

    // Reset when event is cleared so we can fly to the same event again if re-selected
    if (!eventId) {
      lastFlyToIdRef.current = null;
    }
  }, [eventId, map]);

  return null;
}

/**
 * Layer type definitions for the icon-based layer switcher.
 */
type LayerType = 'street' | 'satellite' | 'satellite-labels' | 'terrain';

// ESRI World Imagery as reliable base layer (global coverage at all zoom levels)
const ESRI_IMAGERY_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

// NSW Imagery via CloudFront cache (7-day TTL) - overlaid on top for higher resolution
// 10cm in towns, 50cm across NSW. Only has data at certain zoom levels/areas.
const NSW_IMAGERY_URL = `${import.meta.env.VITE_TILE_BASE_URL || 'https://d3c2yw8hurho5b.cloudfront.net'}/tiles/nsw/{z}/{y}/{x}`;

// `bg` fills the map container behind the tiles. When a tile hasn't painted
// yet (fast pan, fresh zoom level) the gap shows this colour instead of a
// white flash — dark to match satellite imagery, light-grey to match the
// street/terrain basemaps. This is the single biggest fix for the "white
// box" seam; it turns a missing tile into an unobtrusive "loading" tone.
// Zoom thresholds for the premium-imagery gate:
//  - FREE_MAX_ZOOM: deepest zoom the free global satellite imagery still has
//    native tiles for. Beyond this, standard imagery shows "map data not
//    available" placeholders, so free users are held here and offered HD.
//  - HD_MAX_ZOOM: how deep supporters can go (NSW SIX high-res reaches z21).
const FREE_MAX_ZOOM = 19;
const HD_MAX_ZOOM = 21;

// `maxNativeZoom` per base layer: the deepest level the provider actually has
// tiles for. Leaflet up-scales the last native tile past this instead of
// showing a blank/"data not available" seam, so we can allow the map to zoom
// beyond it (for the HD overlay / the free-tier gate bounce) without gaps.
const LAYER_CONFIG: Record<LayerType, { label: string; url: string; attribution: string; maxNativeZoom?: number; bg: string }> = {
  street: {
    label: 'Street',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxNativeZoom: 19,
    bg: '#e5e3df', // OSM land tone
  },
  satellite: {
    label: 'Satellite',
    url: ESRI_IMAGERY_URL,
    attribution: 'Tiles &copy; Esri',
    maxNativeZoom: 19,
    bg: '#0b1220', // dark, matches imagery ocean/shadow
  },
  'satellite-labels': {
    label: 'Hybrid',
    url: ESRI_IMAGERY_URL,
    attribution: 'Tiles &copy; Esri',
    maxNativeZoom: 19,
    bg: '#0b1220',
  },
  terrain: {
    label: 'Terrain',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: 'Map tiles by OpenTopoMap, under CC BY-SA 3.0',
    maxNativeZoom: 17,
    bg: '#e6e2d8', // topo cream
  },
};

/**
 * ZoomUpsellGate — caps how far free users can zoom on satellite imagery and
 * turns the old "map data not available" dead-end into a supporter prompt.
 *
 * Supporters (highResLayers) get the full HD range. Free users on a satellite
 * base are allowed one level past the free cap so a zoom-in gesture registers,
 * then bounced back with the SupporterModal offering HD imagery. On non-
 * satellite bases (where HD wouldn't help) we just cap the zoom, no upsell.
 */
function ZoomUpsellGate({ activeLayer }: { activeLayer: LayerType }) {
  const map = useMap();
  const { hasFeature } = useSupporter();
  const hasHighRes = hasFeature('highResLayers');
  const [showModal, setShowModal] = useState(false);
  const lastPromptRef = useRef(0);

  useEffect(() => {
    const onSatellite = activeLayer === 'satellite' || activeLayer === 'satellite-labels';

    // Supporters: full range, no gate.
    if (hasHighRes) {
      map.setMaxZoom(HD_MAX_ZOOM);
      return;
    }

    // Free users off satellite: HD is irrelevant, just cap at the free limit.
    if (!onSatellite) {
      map.setMaxZoom(FREE_MAX_ZOOM);
      if (map.getZoom() > FREE_MAX_ZOOM) map.setZoom(FREE_MAX_ZOOM);
      return;
    }

    // Free users on satellite: allow one level past so the gesture registers,
    // then bounce back and offer HD. maxNativeZoom scaling means that extra
    // level shows scaled imagery, never a "data not available" seam.
    map.setMaxZoom(FREE_MAX_ZOOM + 1);
    if (map.getZoom() > FREE_MAX_ZOOM + 1) map.setZoom(FREE_MAX_ZOOM + 1);

    const onZoomEnd = () => {
      if (map.getZoom() > FREE_MAX_ZOOM) {
        const now = Date.now();
        // Throttle so repeated pinches don't machine-gun the modal.
        if (now - lastPromptRef.current > 5000) {
          lastPromptRef.current = now;
          setShowModal(true);
        }
        map.setZoom(FREE_MAX_ZOOM, { animate: true });
      }
    };
    map.on('zoomend', onZoomEnd);
    return () => {
      map.off('zoomend', onZoomEnd);
    };
  }, [map, hasHighRes, activeLayer]);

  return showModal ? <SupporterModal onClose={() => setShowModal(false)} source="zoom_gate" /> : null;
}

/**
 * Mobile-friendly icon-based layer switcher control.
 * Shows a row of icon buttons instead of text dropdown.
 */
function MobileLayerControl({ onMyLocation, isLocating, highResEnabled = false, activeLayer, onLayerChange }: { onMyLocation?: () => void; isLocating?: boolean; highResEnabled?: boolean; activeLayer: LayerType; onLayerChange: (layer: LayerType) => void }) {
  const map = useMap();
  // NSW high-res imagery is a supporter perk — only overlay it when the user
  // both holds the entitlement and has the toggle on.
  const { hasFeature } = useSupporter();
  const hasHighRes = hasFeature('highResLayers');
  const [isExpanded, setIsExpanded] = useState(false);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const labelsLayerRef = useRef<L.TileLayer | null>(null);
  const nswOverlayRef = useRef<L.TileLayer | null>(null);

  // Initialize tile layer
  useEffect(() => {
    const config = LAYER_CONFIG[activeLayer];

    // Remove existing layers (the NSW high-res overlay is managed in its own
    // effect below so toggling it doesn't rebuild the base tiles).
    if (tileLayerRef.current) {
      map.removeLayer(tileLayerRef.current);
    }
    if (labelsLayerRef.current) {
      map.removeLayer(labelsLayerRef.current);
    }

    // Add new base layer. Tiles wrap horizontally so panning across the
    // antimeridian feels seamless instead of hitting a "Map data not
    // available" grey zone — the multi-Earth concern is already handled
    // by the minZoom cap on MapContainer below.
    //
    // keepBuffer: 4 (default 2) on capable devices, 2 on low-end. Leaflet
    // aggressively unloads off-screen tiles to save memory; a 4-tile buffer
    // pre-renders enough that small pans never reach unloaded tiles, but
    // at ~3-4× the tile cache footprint. On a 4 GB Android phone with
    // three stacked tile layers that bloats RAM by ~60 MB — enough to
    // make Chrome evict the tab mid-emergency. Drop to 2 on low-end.
    // Labels-only overlay uses 1 unconditionally (brief flash of label
    // gaps during pan is invisible behind the base tiles).
    const lowEnd = isLowEndDevice();
    const baseKeepBuffer = lowEnd ? 2 : 4;

    // Match the container background to this basemap so a not-yet-loaded
    // tile reads as "loading" rather than flashing white against imagery.
    map.getContainer().style.backgroundColor = config.bg;

    tileLayerRef.current = L.tileLayer(config.url, {
      attribution: config.attribution,
      // Allow zooming past the provider's native tiles; Leaflet up-scales the
      // last native level (crisp→soft, the Google/Windy look) rather than
      // showing a "map data not available" seam. The map's effective max zoom
      // is governed by ZoomUpsellGate (free vs supporter), not this value.
      maxZoom: HD_MAX_ZOOM,
      maxNativeZoom: config.maxNativeZoom ?? 19,
      keepBuffer: baseKeepBuffer,
      // On mobile Leaflet defaults updateWhenIdle=true, so base tiles load
      // only AFTER a pan stops — the main cause of the white gap while
      // dragging. Load continuously during the pan on capable devices;
      // keep the deferred behaviour on low-end to limit request churn.
      updateWhenIdle: lowEnd,
      // Don't fetch intermediate zoom levels mid-pinch: Leaflet scales the
      // existing tiles (blurry → sharp, the Google/Windy look) which reads
      // far better than flashing to the background and back.
      updateWhenZooming: false,
    }).addTo(map);

    // Add labels layer for satellite-labels (ESRI Reference Overlay for denser place names)
    if (activeLayer === 'satellite-labels') {
      labelsLayerRef.current = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
        attribution: 'Labels &copy; Esri',
        maxZoom: HD_MAX_ZOOM,
        maxNativeZoom: 19,
        keepBuffer: 1,
      }).addTo(map);
    }

    return () => {
      if (tileLayerRef.current) {
        map.removeLayer(tileLayerRef.current);
      }
      if (labelsLayerRef.current) {
        map.removeLayer(labelsLayerRef.current);
      }
    };
  }, [map, activeLayer]);

  // NSW high-res imagery overlay (supporter-gated). Separate effect so toggling
  // HD on/off doesn't tear down and rebuild the base tiles. Only paints on
  // satellite modes at zoom 16+ where NSW SIX has data, and only when the user
  // holds the highResLayers entitlement AND has the toggle on.
  useEffect(() => {
    if (nswOverlayRef.current) {
      map.removeLayer(nswOverlayRef.current);
      nswOverlayRef.current = null;
    }

    const onSatellite = activeLayer === 'satellite' || activeLayer === 'satellite-labels';
    if (!onSatellite || !highResEnabled || !hasHighRes) return;

    nswOverlayRef.current = L.tileLayer(NSW_IMAGERY_URL, {
      attribution: 'HD &copy; <a href="https://www.spatial.nsw.gov.au">NSW Spatial Services</a>',
      minZoom: 16,
      maxZoom: 21,
      keepBuffer: isLowEndDevice() ? 2 : 4,
      updateWhenIdle: true,
      updateWhenZooming: false,
    }).addTo(map);

    return () => {
      if (nswOverlayRef.current) {
        map.removeLayer(nswOverlayRef.current);
        nswOverlayRef.current = null;
      }
    };
  }, [map, activeLayer, highResEnabled, hasHighRes]);

  const handleLayerChange = (layer: LayerType) => {
    onLayerChange(layer);
    setIsExpanded(false);
  };

  return (
    <div className="map-control-stack">
      {/* Layer control toggle button */}
      <button
        className={`map-control-btn ${isExpanded ? 'expanded' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title="Change map style"
      >
        <MapLayerIcon type={activeLayer} />
      </button>

      {/* My Location button */}
      {onMyLocation && (
        <button
          className="map-control-btn"
          onClick={onMyLocation}
          disabled={isLocating}
          title="Go to my location"
        >
          {isLocating ? (
            <span className="map-control-spinner" />
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
            </svg>
          )}
        </button>
      )}

      {/* Expanded layer options */}
      {isExpanded && (
        <div className="map-layer-options">
          {(Object.keys(LAYER_CONFIG) as LayerType[]).map((layerType) => (
            <button
              key={layerType}
              className={`map-layer-option ${activeLayer === layerType ? 'active' : ''}`}
              onClick={() => handleLayerChange(layerType)}
              title={LAYER_CONFIG[layerType].label}
            >
              <MapLayerIcon type={layerType} />
              <span className="map-layer-label">{LAYER_CONFIG[layerType].label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Component to track map bounds changes and report them to parent.
 * PERFORMANCE: Uses ref to avoid re-running effect when callback changes
 */
function BoundsTracker({ onBoundsChange }: { onBoundsChange?: (bounds: MapBounds) => void }) {
  const map = useMap();
  const callbackRef = useRef(onBoundsChange);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep callback ref updated without triggering effect
  useEffect(() => {
    callbackRef.current = onBoundsChange;
  }, [onBoundsChange]);

  useEffect(() => {
    const updateBounds = () => {
      // Debounce to prevent rapid updates
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (!callbackRef.current) return;
        const bounds = map.getBounds();
        callbackRef.current({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
      }, 150);
    };

    // Initial bounds (with slight delay to let map settle)
    const initialTimeout = setTimeout(updateBounds, 100);

    // Update on map move/zoom
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      clearTimeout(initialTimeout);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  return null;
}

/**
 * Format event popup content.
 * PERFORMANCE: Wind data is cached to prevent redundant API calls
 */
const EventPopup = memo(function EventPopup({ event, onView3D, onRequestAuth }: { event: UnifiedEvent; onView3D?: (event: UnifiedEvent) => void; onRequestAuth?: () => void }) {
  const info = EVENT_TYPE_INFO[event.type];
  const { isAuthenticated } = useAuth();
  const [windData, setWindData] = useState<WindData | null>(null);
  const [isLoadingWind, setIsLoadingWind] = useState(false);

  // Fetch wind data for fire incidents with caching
  useEffect(() => {
    if (event.type !== 'FIRE_INCIDENT') return;

    // Generate cache key for this location (round to 2 decimal places to group nearby locations)
    const cacheKey = `${event.location.lat.toFixed(2)},${event.location.lon.toFixed(2)}`;

    // Check cache first
    if (windDataCache[cacheKey]) {
      setWindData(windDataCache[cacheKey]);
      return;
    }

    const loadWindData = async () => {
      setIsLoadingWind(true);
      try {
        const data = await fetchWindData({
          lat: event.location.lat,
          lon: event.location.lon
        });
        // Cache the result
        windDataCache[cacheKey] = data;
        setWindData(data);
      } catch (error) {
        console.error('Failed to load wind data for popup:', error);
      } finally {
        setIsLoadingWind(false);
      }
    };

    loadWindData();
  }, [event.id, event.type, event.location.lat, event.location.lon]);

  // Get severity background color for fire incidents
  const getSeverityBgColor = (severity: unknown): string => {
    switch (severity) {
      case 'EMERGENCY': return '#dc2626';
      case 'WATCH_AND_ACT': return '#f97316';
      case 'ADVICE': return '#eab308';
      default: return '#6b7280';
    }
  };

  // Get text color for severity (black for yellow/advice, white for others)
  const getSeverityTextColor = (severity: unknown): string => {
    return severity === 'ADVICE' ? '#000000' : '#ffffff';
  };

  // statusColor lives in utils/severity.ts now — single source of truth.

  const severity = event.data?.severity as string | undefined;
  const severityBgColor = getSeverityBgColor(severity);
  const severityTextColor = getSeverityTextColor(severity);

  return (
    <div className="event-popup">
      {/* Fire incident header - matches FireDetailPanel style */}
      {event.type === 'FIRE_INCIDENT' ? (
        <div style={{
          backgroundColor: severityBgColor,
          color: severityTextColor,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Fire marker icon */}
          <FireMarkerIcon
            severity={severity || 'ADVICE'}
            size="medium"
            style={{ flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '10px',
              opacity: 0.9,
              marginBottom: '2px',
              textTransform: 'uppercase',
              fontWeight: '600',
              letterSpacing: '0.5px',
            }}>
              {event.data?.alertLevel ? String(event.data.alertLevel) : 'Fire Incident'}
            </div>
            <h3 style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '700',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>
              {event.title}
            </h3>
          </div>
        </div>
      ) : (
        /* Non-fire event header */
        <div className="event-popup-header" style={{ padding: '12px 16px', backgroundColor: info.color, color: 'white', width: '100%', boxSizing: 'border-box' }}>
          <span className="event-popup-icon">{info.icon}</span>
          <span className="event-popup-type" style={{ fontWeight: '600' }}>{info.label}</span>
          <h3 style={{ margin: '8px 0 0 0', fontSize: '14px', fontWeight: '700' }}>{event.title}</h3>
        </div>
      )}

      {/* Content area */}
      <div style={{ padding: '12px 16px' }}>
        {/* Status badge for fires */}
        {event.type === 'FIRE_INCIDENT' && event.data?.status !== undefined && (
          <div style={{ marginBottom: '10px' }}>
            <span style={{
              backgroundColor: statusColor(event.data.status as string | undefined),
              color: 'white',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {String(event.data.status).replace(/_/g, ' ')}
            </span>
          </div>
        )}

      {event.description ? (
        <p className="event-popup-description">{event.description}</p>
      ) : null}

      {/* Rich fire details */}
      {event.type === 'FIRE_INCIDENT' && event.data ? (
        <div className="event-popup-details" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          marginTop: '8px',
          fontSize: '13px'
        }}>
          {event.data.councilArea ? (
            <div><strong>Council Area:</strong> {String(event.data.councilArea)}</div>
          ) : null}
          {(event.data.fireType || event.data.category2 || event.data.capEvent) ? (
            <div><strong>Type:</strong> {String(event.data.fireType || event.data.category2 || event.data.capEvent)}</div>
          ) : null}
          {event.data.sizeHectares && Number(event.data.sizeHectares) > 0 ? (
            <div><strong>Size:</strong> {Number(event.data.sizeHectares).toLocaleString()} hectares</div>
          ) : null}
          {event.data.responsibleAgency ? (
            <div><strong>Agency:</strong> {String(event.data.responsibleAgency)}</div>
          ) : null}
          {event.data.resourceCount && Number(event.data.resourceCount) > 0 ? (
            <div><strong>Resources:</strong> {String(event.data.resourceCount)} vehicles</div>
          ) : null}
          {event.data.lastUpdated ? (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
              Updated: {formatRelativeTime(String(event.data.lastUpdated))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Wind data for fire incidents */}
      {event.type === 'FIRE_INCIDENT' && (
        <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
          {isLoadingWind ? (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Loading wind data...</div>
          ) : windData && windData.wind_speed_kmh !== null && windData.wind_direction_degrees !== null ? (
            <div style={{
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              padding: '10px',
              borderRadius: '6px',
              border: '1px solid rgba(59, 130, 246, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '18px' }}>💨</span>
                <strong style={{ fontSize: '13px', color: '#60a5fa' }}>Wind Conditions</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Wind direction compass arrow */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  border: '2px solid #3b82f6',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ position: 'absolute', top: '1px', fontSize: '7px', fontWeight: '600', color: '#60a5fa' }}>N</span>
                  <span style={{ position: 'absolute', bottom: '1px', fontSize: '7px', fontWeight: '600', color: '#60a5fa' }}>S</span>
                  <span style={{ position: 'absolute', left: '2px', fontSize: '7px', fontWeight: '600', color: '#60a5fa' }}>W</span>
                  <span style={{ position: 'absolute', right: '2px', fontSize: '7px', fontWeight: '600', color: '#60a5fa' }}>E</span>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    style={{
                      transform: `rotate(${(windData.wind_direction_degrees ?? 0) + 180}deg)`,
                    }}
                  >
                    <path
                      d="M11 2 L14 9 L12 8 L12 18 L10 18 L10 8 L8 9 Z"
                      fill="#3b82f6"
                      stroke="#60a5fa"
                      strokeWidth="0.5"
                    />
                  </svg>
                </div>
                {/* Wind info text */}
                <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                  <div><strong>Speed:</strong> {Math.round(windData.wind_speed_kmh)} km/h</div>
                  <div><strong>Direction:</strong> {getWindDirection(windData.wind_direction_degrees)} ({windData.wind_direction_degrees}°)</div>
                  {windData.wind_gusts_kmh && windData.wind_gusts_kmh > 0 ? (
                    <div><strong>Gusts:</strong> {Math.round(windData.wind_gusts_kmh)} km/h</div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

        <div className="event-popup-meta" style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border-color)', fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
          <span className="event-popup-time">{formatRelativeTime(event.observedAt)}</span>
          {event._distanceKm !== undefined && (
            <span className="event-popup-distance"> &middot; {event._distanceKm.toFixed(1)} km away</span>
          )}
        </div>

        {/* More Info Button */}
        {event.type === 'FIRE_INCIDENT' && event.data?.moreInfoUrl ? (
          <div style={{ marginTop: '12px' }}>
            <a
              href={String(event.data.moreInfoUrl)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                width: '100%',
                padding: '10px 16px',
                backgroundColor: '#dc2626',
                color: 'white',
                textAlign: 'center',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '13px',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            >
              View RFS Information
            </a>
          </div>
        ) : null}

        {/* View in 3D Button - available for all fire incidents */}
        {/* Requires authentication - prompts login if not signed in */}
        {event.type === 'FIRE_INCIDENT' && (onView3D || onRequestAuth) ? (
          <div style={{ marginTop: '8px' }}>
            <button
              onClick={() => {
                if (isAuthenticated && onView3D) {
                  onView3D(event);
                } else if (onRequestAuth) {
                  onRequestAuth();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 16px',
                backgroundColor: '#7c3aed',
                color: 'white',
                textAlign: 'center',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '13px',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6d28d9'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7c3aed'}
            >
              <span>🌐</span>
              {isAuthenticated ? 'View in 3D Terrain' : 'Sign in for 3D View'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
});

/**
 * Compare events arrays by ID to prevent unnecessary re-renders
 * when the array reference changes but content is the same
 */
function areEventsEqual(prevEvents: UnifiedEvent[], nextEvents: UnifiedEvent[]): boolean {
  if (prevEvents.length !== nextEvents.length) return false;
  // Compare by ID since that's the stable identifier
  const prevIds = prevEvents.map(e => e.id).sort().join(',');
  const nextIds = nextEvents.map(e => e.id).sort().join(',');
  return prevIds === nextIds;
}

/**
 * Custom comparison for Map props to prevent popup flickering
 * Only re-render when meaningful props change, not on every callback reference change
 */
function areMapPropsEqual(prevProps: MapProps, nextProps: MapProps): boolean {
  // These props should trigger re-render
  if (!areEventsEqual(prevProps.events, nextProps.events)) return false;
  if (prevProps.center.lat !== nextProps.center.lat) return false;
  if (prevProps.center.lon !== nextProps.center.lon) return false;
  if (prevProps.viewRadiusKm !== nextProps.viewRadiusKm) return false;
  if (prevProps.isVisible !== nextProps.isVisible) return false;
  if (prevProps.showWindLayer !== nextProps.showWindLayer) return false;
  if (prevProps.showAircraftLayer !== nextProps.showAircraftLayer) return false;
  if (prevProps.showFireBoundaries !== nextProps.showFireBoundaries) return false;
  if (prevProps.showWarningAreas !== nextProps.showWarningAreas) return false;
  if (prevProps.showHotspots !== nextProps.showHotspots) return false;
  if (prevProps.showRoadClosures !== nextProps.showRoadClosures) return false;
  if (prevProps.showPowerOutages !== nextProps.showPowerOutages) return false;
  if (prevProps.showSmoke !== nextProps.showSmoke) return false;
  if (prevProps.showAirQuality !== nextProps.showAirQuality) return false;
  if (prevProps.showCityTemperatures !== nextProps.showCityTemperatures) return false;
  if (prevProps.showThunderstormWarnings !== nextProps.showThunderstormWarnings) return false;
  if (prevProps.showLightningRisk !== nextProps.showLightningRisk) return false;
  if (prevProps.showHighResImagery !== nextProps.showHighResImagery) return false;
  if (prevProps.showSatelliteImagery !== nextProps.showSatelliteImagery) return false;
  if (prevProps.selectedEvent?.id !== nextProps.selectedEvent?.id) return false;
  if (prevProps.isLocating !== nextProps.isLocating) return false;
  if (prevProps.recenterTrigger !== nextProps.recenterTrigger) return false;
  if (prevProps.userLocation?.lat !== nextProps.userLocation?.lat) return false;
  if (prevProps.userLocation?.lon !== nextProps.userLocation?.lon) return false;

  // Compare photos by ID
  const prevPhotoIds = (prevProps.photos || []).map(p => p.id).sort().join(',');
  const nextPhotoIds = (nextProps.photos || []).map(p => p.id).sort().join(',');
  if (prevPhotoIds !== nextPhotoIds) return false;

  // Compare editor preview circle
  const prevPreview = prevProps.editorPreviewCircle;
  const nextPreview = nextProps.editorPreviewCircle;
  if ((prevPreview === null) !== (nextPreview === null)) return false;
  if (prevPreview && nextPreview) {
    if (prevPreview.center.lat !== nextPreview.center.lat) return false;
    if (prevPreview.center.lon !== nextPreview.center.lon) return false;
    if (prevPreview.radiusKm !== nextPreview.radiusKm) return false;
  }

  // Compare zone drawing props
  if (prevProps.isDrawingZone !== nextProps.isDrawingZone) return false;
  if (prevProps.zoneShapeType !== nextProps.zoneShapeType) return false;

  // Compare watch zones by IDs
  const prevZoneIds = (prevProps.watchZones || []).map(z => z.zoneId).sort().join(',');
  const nextZoneIds = (nextProps.watchZones || []).map(z => z.zoneId).sort().join(',');
  if (prevZoneIds !== nextZoneIds) return false;

  // Compare visible zone IDs
  const prevVisibleIds = prevProps.visibleZoneIds ? Array.from(prevProps.visibleZoneIds).sort().join(',') : '';
  const nextVisibleIds = nextProps.visibleZoneIds ? Array.from(nextProps.visibleZoneIds).sort().join(',') : '';
  if (prevVisibleIds !== nextVisibleIds) return false;

  // Callbacks don't need to trigger re-render (they're stored in refs inside components)
  // onBoundsChange, onEventClick, onPhotoRemove, onMyLocation, onMapClick, onZoneDrawn, onZoneClick are all handled via refs

  return true;
}

/** Stable bounds representation using primitives to prevent infinite re-renders */
interface StableBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Component that renders event markers with viewport-based filtering.
 * Only renders markers that are within the current map viewport + buffer.
 * PERFORMANCE: Significantly reduces DOM nodes when zoomed into a small area.
 */
function ViewportFilteredMarkers({
  events,
  onEventClick,
  selectedEventId,
  onView3D,
  onRequestAuth,
  showFireIncidents = true
}: {
  events: UnifiedEvent[];
  onEventClick?: (event: UnifiedEvent) => void;
  selectedEventId?: string;
  onView3D?: (event: UnifiedEvent) => void;
  onRequestAuth?: () => void;
  showFireIncidents?: boolean;
}) {
  const map = useMap();
  const [stableBounds, setStableBounds] = useState<StableBounds | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(map.getZoom());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track previous visible events to avoid unnecessary re-renders
  const prevVisibleRef = useRef<{ ids: string; events: UnifiedEvent[] }>({ ids: '', events: [] });
  // Store callback in ref to prevent re-renders when parent re-renders
  const onEventClickRef = useRef(onEventClick);
  onEventClickRef.current = onEventClick;
  // Store selected event ID in ref to use in memoized content
  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;

  // Minimum zoom level to show BURN_AREA icons (polygon is always shown)
  const BURN_AREA_MIN_ZOOM = 13;

  // Track viewport bounds and zoom level - store as primitives to prevent object reference changes
  useEffect(() => {
    const updateBounds = () => {
      // Debounce to prevent rapid updates during popup auto-pan
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        // Store as primitives to prevent object reference changes causing re-renders
        setStableBounds({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        });
        setCurrentZoom(map.getZoom());
      }, 100);
    };

    // Initial bounds (with delay to let map settle)
    const initialTimeout = setTimeout(() => {
      const bounds = map.getBounds();
      setStableBounds({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      });
      setCurrentZoom(map.getZoom());
    }, 100);

    // Update on map movement
    map.on('moveend', updateBounds);
    map.on('zoomend', updateBounds);

    return () => {
      clearTimeout(initialTimeout);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      map.off('moveend', updateBounds);
      map.off('zoomend', updateBounds);
    };
  }, [map]);

  // Filter events to only those in viewport (with 20% buffer for smooth panning)
  // IMPORTANT: Only return new array if the set of visible IDs actually changed
  // Also excludes the selected event which is rendered separately for stability
  // Also excludes FIRE_INCIDENT and BURN_AREA events if showFireIncidents is false
  // Also excludes BURN_AREA icons when zoomed out (polygon is still shown via FireBoundaryLayer)
  const visibleEvents = useMemo(() => {
    // Pre-filter by event type if fire incidents are hidden
    let filteredEvents = events;
    if (!showFireIncidents) {
      filteredEvents = events.filter(e => e.type !== 'FIRE_INCIDENT' && e.type !== 'BURN_AREA');
    }

    // Hide BURN_AREA icons when zoomed out (polygon is still visible via FireBoundaryLayer)
    // This prevents the map from being overwhelmed with burn area markers
    if (currentZoom < BURN_AREA_MIN_ZOOM) {
      filteredEvents = filteredEvents.filter(e => e.type !== 'BURN_AREA');
    }

    if (!stableBounds) return filteredEvents.filter(e => e.id !== selectedEventId);

    // Add buffer to bounds (20% on each side) to prevent pop-in during panning
    const latBuffer = (stableBounds.north - stableBounds.south) * 0.2;
    const lngBuffer = (stableBounds.east - stableBounds.west) * 0.2;

    const bufferedSouth = stableBounds.south - latBuffer;
    const bufferedNorth = stableBounds.north + latBuffer;
    const bufferedWest = stableBounds.west - lngBuffer;
    const bufferedEast = stableBounds.east + lngBuffer;

    const visible = filteredEvents.filter(event => {
      // Exclude selected event - it's rendered separately
      if (event.id === selectedEventId) return false;
      const { lat, lon } = event.location;
      return lat >= bufferedSouth && lat <= bufferedNorth &&
             lon >= bufferedWest && lon <= bufferedEast;
    });

    // Check if the set of visible event IDs changed
    const newIds = visible.map(e => e.id).sort().join(',');
    if (newIds === prevVisibleRef.current.ids) {
      // Same events visible - return the previous array to prevent re-render
      return prevVisibleRef.current.events;
    }

    // Log filtering stats for debugging (only when count changes significantly)
    if (filteredEvents.length > 0 && visible.length !== filteredEvents.length - (selectedEventId ? 1 : 0)) {
      console.log(`[Viewport Filter] Rendering ${visible.length}/${filteredEvents.length} markers in view`);
    }

    // Store for next comparison
    prevVisibleRef.current = { ids: newIds, events: visible };
    return visible;
  }, [events, stableBounds?.north, stableBounds?.south, stableBounds?.east, stableBounds?.west, selectedEventId, showFireIncidents, currentZoom]);

  // Get the selected event object for separate rendering
  const selectedEvent = selectedEventId ? events.find(e => e.id === selectedEventId) : null;

  // Ref for selected marker to auto-open popup
  const selectedMarkerRef = useRef<L.Marker | null>(null);

  // Auto-open popup when selected event changes
  useEffect(() => {
    if (selectedMarkerRef.current && selectedEvent) {
      // Small delay to ensure marker is fully rendered
      setTimeout(() => {
        selectedMarkerRef.current?.openPopup();
      }, 50);
    }
  }, [selectedEvent?.id]);

  // Separate events by severity for z-index layering
  // EMERGENCY fires must ALWAYS be on top - this is a safety-critical feature
  const { emergencyEvents, watchAndActEvents, regularEvents } = useMemo(() => {
    const emergency: UnifiedEvent[] = [];
    const watchAndAct: UnifiedEvent[] = [];
    const regular: UnifiedEvent[] = [];

    for (const event of visibleEvents) {
      const severity = event.data?.severity as string;
      if (severity === 'EMERGENCY') {
        emergency.push(event);
      } else if (severity === 'WATCH_AND_ACT') {
        watchAndAct.push(event);
      } else {
        regular.push(event);
      }
    }

    return { emergencyEvents: emergency, watchAndActEvents: watchAndAct, regularEvents: regular };
  }, [visibleEvents]);

  return (
    // Fire event markers live in a dedicated pane ABOVE the default marker pane
    // (600) but below tooltips (650) / popups (700), so fire markers always sit
    // on top of every other marker type (hotspots, power outages, aircraft,
    // road closures) while their popups still open on top of everything.
    // (user feedback)
    <Pane name="fireEvents" style={{ zIndex: 640 }}>
      {/* Selected marker rendered separately outside cluster for stability */}
      {selectedEvent && (
        <Marker
          ref={selectedMarkerRef}
          key={`selected-${selectedEvent.id}`}
          position={[selectedEvent.location.lat, selectedEvent.location.lon]}
          icon={createEventIcon(selectedEvent)}
          zIndexOffset={15000}
          eventHandlers={{
            click: () => onEventClickRef.current?.(selectedEvent),
          }}
        >
          <Popup
            className="fire-event-popup"
            autoPan={false}
            closeOnClick={false}
          >
            <EventPopup event={selectedEvent} onView3D={onView3D} onRequestAuth={onRequestAuth} />
          </Popup>
        </Marker>
      )}

      {/* WATCH AND ACT alerts - high priority, rendered on top of regular markers */}
      {watchAndActEvents.map((event) => (
        <Marker
          key={`watch-${event.id}`}
          position={[event.location.lat, event.location.lon]}
          icon={createEventIcon(event)}
          zIndexOffset={5000}
          eventHandlers={{
            click: () => onEventClickRef.current?.(event),
          }}
        >
          <Popup
            className="fire-event-popup"
            autoPan={false}
            closeOnClick={false}
          >
            <EventPopup event={event} onView3D={onView3D} onRequestAuth={onRequestAuth} />
          </Popup>
        </Marker>
      ))}

      {/* EMERGENCY ALERTS - HIGHEST PRIORITY - ALWAYS on top of everything
          These red triangles must never be hidden by other markers - safety critical */}
      {emergencyEvents.map((event) => (
        <Marker
          key={`emergency-${event.id}`}
          position={[event.location.lat, event.location.lon]}
          icon={createEventIcon(event)}
          zIndexOffset={10000}
          eventHandlers={{
            click: () => onEventClickRef.current?.(event),
          }}
        >
          <Popup
            className="fire-event-popup"
            autoPan={false}
            closeOnClick={false}
          >
            <EventPopup event={event} onView3D={onView3D} onRequestAuth={onRequestAuth} />
          </Popup>
        </Marker>
      ))}

      {/* Regular/lower-severity markers — rendered individually (NOT clustered).
          Clustering was reverted: consolidating fire events into a numbered
          bubble hid individual incidents, which users need to see. (user
          feedback) */}
      {regularEvents.map((event) => (
        <Marker
          key={event.id}
          position={[event.location.lat, event.location.lon]}
          icon={createEventIcon(event)}
          eventHandlers={{
            click: () => onEventClickRef.current?.(event),
          }}
        >
          <Popup
            className="fire-event-popup"
            autoPan={false}
            closeOnClick={false}
          >
            <EventPopup event={event} onView3D={onView3D} onRequestAuth={onRequestAuth} />
          </Popup>
        </Marker>
      ))}
    </Pane>
  );
}

/**
 * Main Map component with performance optimizations.
 * PERFORMANCE: Wrapped with memo() with custom comparison to prevent popup flickering
 */
const Map = memo(function Map({ events, center, viewRadiusKm, recenterTrigger, isVisible = true, onEventClick, onBoundsChange, showWindLayer, showAircraftLayer, showFireBoundaries, showWarningAreas, showHotspots, showRoadClosures, showPowerOutages, showSmoke, showAirQuality, showCityTemperatures, showThunderstormWarnings, showLightningRisk, showHighResImagery, showSatelliteImagery, selectedEvent, photos, onRequestRemoval, onMyLocation, isLocating, onMapClick, editorPreviewCircle, isDrawingZone, zoneShapeType, onZoneDrawn, editingZone, watchZones, visibleZoneIds, onZoneClick, onZoneEdit, onView3D, onRequestAuth, onDeselectEvent, userLocation, onAircraftRideAlong }: MapProps) {
  const mapRef = useRef<L.Map>(null);
  // Active base map style, lifted out of MobileLayerControl so the
  // ZoomUpsellGate knows whether we're on a satellite base (where HD applies).
  const [activeBaseLayer, setActiveBaseLayer] = useState<LayerType>('satellite-labels');

  return (
    <MapContainer
      center={[center.lat, center.lon]}
      zoom={11}
      // Cap how far users can zoom out. At zoom < 3 the world tile set
      // is small enough that 4–5 side-by-side copies of Earth fit on a
      // desktop viewport, which is disorienting for an Australia-focused
      // tool. At zoom 3 the world fits roughly once on most viewports,
      // covering Australia + NZ + surrounding Pacific comfortably.
      // Panning is left unconstrained, and tile layers still wrap
      // horizontally so panning across the antimeridian is seamless.
      minZoom={3}
      // tapTolerance: how far a finger can move during a touch and still
      // register as a tap (vs being treated as the start of a pan).
      // Leaflet's default of 15 px is tight for finger tapping on small
      // markers — bumping to 25 px makes single-finger marker taps land
      // reliably without making intentional pan gestures feel sluggish.
      tapTolerance={25}
      className={`map-container ${onMapClick ? 'editing-zone' : ''}`}
      ref={mapRef}
    >
      {/* Custom icon-based layer and location control stack */}
      <MobileLayerControl
        onMyLocation={onMyLocation}
        isLocating={isLocating}
        highResEnabled={showHighResImagery ?? false}
        activeLayer={activeBaseLayer}
        onLayerChange={setActiveBaseLayer}
      />

      {/* Free-tier zoom cap + HD upsell (replaces the "map data not available"
          dead-end when free users zoom past standard imagery). */}
      <ZoomUpsellGate activeLayer={activeBaseLayer} />

      {/* Compact "forecast valid HH:MM" chip for active model layers */}
      <DataFreshnessChip />

      <MapRecenter center={center} viewRadiusKm={viewRadiusKm} recenterTrigger={recenterTrigger} />
      <MapVisibilityHandler isVisible={isVisible} />
      <FlyToEvent event={selectedEvent} />
      <BoundsTracker onBoundsChange={onBoundsChange} />

      {/* Map click handler for watch zone editor */}
      {onMapClick && <MapClickHandler onClick={onMapClick} />}

      {/* Map click handler for deselecting events (mobile UX) */}
      {onDeselectEvent && selectedEvent && (
        <MapDeselectHandler onDeselect={onDeselectEvent} />
      )}

      {/* Editor preview circle and center marker - shown when creating/editing a watch zone */}
      {editorPreviewCircle && (
        <>
          <Circle
            center={[editorPreviewCircle.center.lat, editorPreviewCircle.center.lon]}
            radius={editorPreviewCircle.radiusKm * 1000}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.15,
              weight: 3,
            }}
          />
          {/* Center marker with crosshairs */}
          <Marker
            position={[editorPreviewCircle.center.lat, editorPreviewCircle.center.lon]}
            icon={L.divIcon({
              className: 'zone-center-marker',
              html: `
                <svg viewBox="0 0 40 40" width="40" height="40">
                  <circle cx="20" cy="20" r="8" fill="#3b82f6" stroke="white" stroke-width="2"/>
                  <line x1="20" y1="4" x2="20" y2="12" stroke="#3b82f6" stroke-width="2"/>
                  <line x1="20" y1="28" x2="20" y2="36" stroke="#3b82f6" stroke-width="2"/>
                  <line x1="4" y1="20" x2="12" y2="20" stroke="#3b82f6" stroke-width="2"/>
                  <line x1="28" y1="20" x2="36" y2="20" stroke="#3b82f6" stroke-width="2"/>
                </svg>
              `,
              iconSize: [40, 40],
              iconAnchor: [20, 20],
            })}
          />
        </>
      )}

      {/* Fire boundary polygons (burn areas) - rendered before markers so they appear behind */}
      {showFireBoundaries && <FireBoundaryLayer events={events} />}

      {/* Warning area polygons (affected zones) - rendered behind burn areas */}
      {showWarningAreas && <WarningAreaLayer events={events} />}

      {/* Recent true-colour satellite imagery (supporter-gated) — sits in the
          tile pane below labels/markers so context stays readable. */}
      <SatelliteImageryLayer enabled={showSatelliteImagery ?? false} />

      {/* Smoke/aerosol layer from NASA GIBS - background layer */}
      <SmokeLayer enabled={showSmoke ?? false} />

      {/* Air quality heatmap layer - PM2.5/AQI from Open-Meteo CAMS */}
      <AirQualityLayer enabled={showAirQuality ?? false} />

      {/* Satellite hotspots layer - rendered before fire markers so they appear behind */}
      <HotspotLayer enabled={showHotspots ?? false} onView3D={onView3D} onRequestAuth={onRequestAuth} />

      {/* Road closures layer - traffic hazards from TfNSW */}
      {showRoadClosures && <RoadClosureLayer />}

      {/* Power outages layer - Ausgrid data */}
      <PowerOutageLayer enabled={showPowerOutages ?? false} />

      {/* Thunderstorm warnings layer - BOM severe weather */}
      <ThunderstormWarningLayer enabled={showThunderstormWarnings ?? false} />

      {/* Lightning risk layer - dry lightning fire danger */}
      <LightningRiskLayer enabled={showLightningRisk ?? false} />

      {/* Event markers with viewport filtering and clustering */}
      <ViewportFilteredMarkers events={events} onEventClick={onEventClick} selectedEventId={selectedEvent?.id} onView3D={onView3D} onRequestAuth={onRequestAuth} />

      {/* Wind particle layer */}
      {showWindLayer && (
        <Suspense fallback={null}>
          <WindParticleLayer />
        </Suspense>
      )}

      {/* Aircraft tracking layer */}
      <AircraftLayer enabled={showAircraftLayer ?? false} onRideAlong={onAircraftRideAlong} />

      {/* City temperature labels - shows when zoomed out */}
      <CityTemperatureLayer enabled={showCityTemperatures ?? true} />

      {/* Photo markers layer */}
      {photos && photos.length > 0 && (
        <PhotoMarkerLayer photos={photos} onRequestRemoval={onRequestRemoval} />
      )}

      {/* Watch zone overlay - shows user's saved zones on the map */}
      {watchZones && visibleZoneIds && (
        <WatchZoneOverlay
          zones={watchZones}
          visibleZoneIds={visibleZoneIds}
          onZoneClick={onZoneClick}
          onZoneEdit={onZoneEdit}
        />
      )}

      {/* User location blue dot */}
      {userLocation && (
        <Marker
          position={[userLocation.lat, userLocation.lon]}
          icon={L.divIcon({
            className: 'user-location-marker',
            html: `
              <div class="user-location-pulse"></div>
              <div class="user-location-dot"></div>
            `,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })}
          interactive={false}
          zIndexOffset={1000}
        />
      )}

      {/* Zone drawing layer - for creating/editing polygon/circle zones */}
      {isDrawingZone && zoneShapeType && onZoneDrawn && (
        <ZoneDrawingLayer
          enabled={isDrawingZone}
          shapeType={zoneShapeType}
          onZoneDrawn={onZoneDrawn}
          initialZone={editingZone || undefined}
        />
      )}
    </MapContainer>
  );
}, areMapPropsEqual);

export default Map;
